import type { BillingCommandAnalyticsEvent } from "./commandTypes";

// A Blocking Approval sends the merchant off the SPA to Shopify, so the plan
// that knows which completion events to fire is gone by the time they return.
// The events ride across the navigation in sessionStorage, which is per-tab and
// therefore starts clean in a fresh tab. Only one Shopify approval can be in
// play at a time, so the key carries no command id.
const STORAGE_KEY = "billing-2026:approval-return:completion-analytics";

type StashedCompletionAnalytics = Readonly<{
  orgId: string;
  events: readonly BillingCommandAnalyticsEvent[];
}>;

const isStash = (value: unknown): value is StashedCompletionAnalytics =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as StashedCompletionAnalytics).orgId === "string" &&
  Array.isArray((value as StashedCompletionAnalytics).events) &&
  (value as StashedCompletionAnalytics).events.every(
    (event) => typeof (event as BillingCommandAnalyticsEvent).type === "string"
  );

export const stashApprovalCompletionAnalytics = (
  value: StashedCompletionAnalytics
): void => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // sessionStorage can be unavailable (private mode, quota). Analytics must
    // never block the merchant's redirect to Shopify, so a failed write is
    // intentionally non-fatal.
  }
};

/**
 * Reads and removes the stash. The events are consumed on arrival, exactly like
 * the `charge_id` marker, so a mid-wait refresh drops both and stays silent.
 * A stash left by a different organization is discarded rather than fired.
 */
export const consumeApprovalCompletionAnalytics = (
  orgId: string
): readonly BillingCommandAnalyticsEvent[] => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return [];
    window.sessionStorage.removeItem(STORAGE_KEY);
    const parsed: unknown = JSON.parse(raw);
    return isStash(parsed) && parsed.orgId === orgId ? parsed.events : [];
  } catch {
    return [];
  }
};
